import { Modal, message } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModalHeader, SearchFilters, Widget, expandIcon } from "datastake-daf/dist/components";
import {
  filtersConfig,
  columnsConfig,
  checkboxConfig,
  selectFiltersConfig,
} from "./config";
import DataSettings from "../DataSettings";
import DafTable from "../../../../../components/Table/newTable";
import { useFilters } from "../../../../../helpers/SubjectSearch";
import { renderRows } from "../../../../Analysis/sca-dds-kyc-widgets/table-components/helper";
import ChannelsService from "../../../../../services/ChannelsService";
import { useSelector } from "react-redux";

export default function DataPoints({ fetchData, mod, data = [], loading: _loading }) {
  const { t } = useTranslation();
  const loadingKeys = useRef({});
  const { id, sourceId } = useParams();
  const [expanded, setExpanded] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const [hasError, setHasError] = useState(false);
  const [toggledKeys, setToggledKeys] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [viewDataItem, setViewDataItem] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectOptions, setSelectOptions] = useState({});
  const { user } = useSelector((state) => state.authentication);

  const loading = _loading || loadingData;

  const dataSource = useMemo(() => data.map((d) => {
    return {
      id: d.id,
      key: d.id,
      datastakeId: d.datastakeId,
      version: d.version,
      label: d.label,
      data: d.data,
      accessibility: d.channel?.accessibility,
      input: d.input,
      channel: d.channel,
      channels: d.channels,
    };
  }), [data]);

  const onGetData = useCallback(async (d) => {
    const confirmed = await modal.confirm({
      title: t('Get Data'),
      content: t('This information will be added to your Store as Acquired Data.'),
      cancelText: t('Cancel'),
      onCancel: () => { },
      okText: t('Confirm'),
      onOk: async () => {
        setLoadingData(true);

        try {
          const { data } = await ChannelsService.acquire(d.channelid);

          if (data.status === 400) {
            message.error(t(data?.response?.message || 'Error occured'));
          } else {
            await fetchData();
            message.success(t('Data has been added to your store!'));
          }
        } catch (err) {
          message.error(t('Error occured'));
        }

        setLoadingData(false);
      },
    });
  }, [modal, t]);

  const onViewDetails = useCallback((val) => setViewDataItem(val), []);

  const view = useMemo(() => `find-information/${id}/${sourceId}`, [id, sourceId]);
  const columns = useMemo(() =>
    columnsConfig({ t, onGetData, onViewDetails, user }), [t, onGetData, onViewDetails, user]);

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
    setPagination,
  } = useFilters({
    selectFiltersConfig,
    view,
    module: mod,
    filtersConfig,
  });

  useEffect(() => {
    setSelectOptions((prev) => ({
      ...prev,
    }));

    setPagination((prev) => ({ ...prev, total: data.length }));
  }, []);

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  const toggle = useCallback((key) => setExpanded((prev) => [...prev, key]), []);
  const unToggle = useCallback((key) => setExpanded((prev) => [...prev.filter((p) => p !== key)]), []);

  const toggleExpand = useCallback((expanded, { key }) =>
    expanded ? toggle(key) : unToggle(key), [toggle, unToggle]);

  return (
    <Widget
      loading={loading}
      title={t('Data Points')}
      className="with-border-header flex-1 h-w-btn-header"
    >
      <div>
        <SearchFilters
          t={t}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          hasError={hasError}
          setHasError={setHasError}
          filtersConfig={newFiltersConfig}
          checkboxConfig={checkboxConfig}
          onSearch={onSearch}
          activeFilters={activeFilters}
          canClear={canClearSearch}
          clearFilters={() => setActiveFilters({})}
        />
      </div>
      <div className='daf-table-wrapper no-pagination'>
        <DafTable
          scrollX={false}
          columns={columns}
          data={dataSource}
          doEmptyRows
          loading={loading}
          pagination={pagination}
          rowKey="key"
          className="no-padding"
          selectOptions={selectOptions}
          filtersConfig={selectFiltersConfig}
          hideOnLoading={false}
          onFilterChange={onFiltersChange}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          defaultFilters={defaultFilters}
          expandable={{
            onExpand: toggleExpand,
            rowExpandable: (a) => {
              return Array.isArray(a.versions);
            },
            expandedRowRender: (d) => renderRows({
              data: d,
              columns,
              expanded: toggledKeys,
              dataKey: 'versions',
            }),
            expandedRowKeys: expanded,
            expandedRowClassName: () => 'expanded-table-cell'
          }}
          expandIcon={(props) => expandIcon({
            ...props,
            arrows: true,
            toggledKeys,
            setToggledKeys,
            loadingKeys,
          })}
          onChange={onTableChange}
        />
      </div>

      <Modal
        footer={null}
        open={!!viewDataItem}
        onCancel={() => setViewDataItem(null)}
        title={(
          <ModalHeader
            title={t('Disclosure Settings')}
            tooltip={{
              title: t('Disclosure Settings'),
              content: t('This feature enables you to set specific rules with regards to how other parties may find, access or use your available information. Of course you may decide to keep it entirely private.'),
            }}
          />
        )}>
        <DataSettings item={viewDataItem} />
      </Modal>

      {contextHolder}
    </Widget>
  );
}
